import { useEffect, useRef, useState } from "react";

/** `null` and `""` both mean no company is selected (US region). */
function normalizeCompanyUUID(companyUUID: string | null): string | null {
  return companyUUID ? companyUUID : null;
}

/**
 * Wizard staging person from `/person/create/empty`. It is created in the
 * selected company's data region (US when no company is selected), so
 * switching company drops it and the next `ensurePersonAvailable` creates one
 * in the new company's region.
 */
export function useStagingPerson(
  companyUUID: string | null,
  createPersonEmpty: (companyUuid: string | null) => Promise<string>,
  isCreating: boolean
) {
  const [personUUID, setPersonUUID] = useState<string | null>(null);
  // undefined: no staging person yet; null: created without a company.
  const personCompanyUUIDRef = useRef<string | null | undefined>(undefined);
  // Company an in-flight create was started for. undefined: nothing in flight.
  const inFlightCompanyRef = useRef<string | null | undefined>(undefined);
  const companyUUIDRef = useRef(normalizeCompanyUUID(companyUUID));
  companyUUIDRef.current = normalizeCompanyUUID(companyUUID);
  // Bumped when a company change or reset invalidates an in-flight create.
  const generationRef = useRef(0);

  useEffect(() => {
    const current = normalizeCompanyUUID(companyUUID);
    const storedForAnotherCompany =
      personCompanyUUIDRef.current !== undefined &&
      personCompanyUUIDRef.current !== current;
    const creatingForAnotherCompany =
      inFlightCompanyRef.current !== undefined &&
      inFlightCompanyRef.current !== current;
    if (storedForAnotherCompany || creatingForAnotherCompany) {
      generationRef.current += 1;
      personCompanyUUIDRef.current = undefined;
      inFlightCompanyRef.current = undefined;
      setPersonUUID(null);
    }
  }, [companyUUID]);

  async function ensurePersonAvailable(): Promise<string> {
    const requestedCompany = companyUUIDRef.current;
    if (personUUID && personCompanyUUIDRef.current === requestedCompany) {
      return personUUID;
    }
    if (isCreating && inFlightCompanyRef.current === requestedCompany) {
      return "";
    }

    const generation = ++generationRef.current;
    // Record the company before the request so a company change while
    // createPersonEmpty is in flight drops this person instead of keeping it.
    inFlightCompanyRef.current = requestedCompany;
    const uuid = await createPersonEmpty(requestedCompany);
    const stillCurrent =
      generation === generationRef.current &&
      companyUUIDRef.current === requestedCompany;
    if (!stillCurrent) {
      if (inFlightCompanyRef.current === requestedCompany) {
        inFlightCompanyRef.current = undefined;
      }
      if (personCompanyUUIDRef.current !== companyUUIDRef.current) {
        personCompanyUUIDRef.current = undefined;
        setPersonUUID(null);
      }
      return "";
    }
    inFlightCompanyRef.current = undefined;
    personCompanyUUIDRef.current = requestedCompany;
    setPersonUUID(uuid);
    return uuid;
  }

  function resetStagingPerson() {
    generationRef.current += 1;
    personCompanyUUIDRef.current = undefined;
    inFlightCompanyRef.current = undefined;
    setPersonUUID(null);
  }

  return { personUUID, ensurePersonAvailable, resetStagingPerson };
}
